import { Container, Sprite, Ticker, type Texture } from 'pixi.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../../../data/constants';

// Largest dimension fits to this many px (source PNG is huge).
const TARGET_SIZE = 25;
// Wall-time travel duration. Real-time deltas (performance.now) so projectile
// stays snappy regardless of BATTLE_PACE_SCALE - matches Particles.ts.
// Exported so the skill can time a dodge to peak at the moment of fly-by.
export const FLIGHT_DURATION_MS = 400;
const SPIN_RAD_PER_SEC = 8;
// Passthrough cleanup: ball self-destructs when it crosses this many px past
// any screen edge (so it doesn't pop visibly while still on-screen).
const OFF_SCREEN_MARGIN = 60;

export class Cannonball extends Container {
  private elapsed = 0;
  private resolveImpact: (() => void) | null = null;
  private impactReached = false;
  private readonly tickerCb: () => void;
  private readonly sprite: Sprite;
  private readonly startX: number;
  private readonly startY: number;
  private readonly targetX: number;
  private readonly targetY: number;
  private readonly passthrough: boolean;
  private readonly ricochetAngleDeg: number | undefined;
  // Per-second velocity. Set during construction for the pre-impact phase;
  // may be overwritten at impact time for ricochet (see update()).
  private vxPerSec: number;
  private vyPerSec: number;
  private lastTime = performance.now();

  constructor(opts: {
    texture: Texture;
    startX: number;
    startY: number;
    targetX: number;
    targetY: number;
    // If true, ball doesn't dispose at impact - flies past target until it
    // leaves the screen. Use when defender blocks (so projectile reads as
    // bouncing off / being deflected through).
    passthrough?: boolean;
    // Angle in degrees between incoming velocity and outgoing (post-impact)
    // velocity. 180 = straight back, 90 = perpendicular up. Smaller values
    // tilt the bounce more upward. Only applied when passthrough is true.
    // If undefined, ball continues along its original trajectory.
    ricochetAngleDeg?: number;
  }) {
    super();
    this.sprite = new Sprite(opts.texture);
    this.sprite.anchor.set(0.5);
    const longest = Math.max(opts.texture.width, opts.texture.height);
    if (longest > 0) this.sprite.scale.set(TARGET_SIZE / longest);
    this.addChild(this.sprite);
    this.position.set(opts.startX, opts.startY);
    this.startX = opts.startX;
    this.startY = opts.startY;
    this.targetX = opts.targetX;
    this.targetY = opts.targetY;
    this.passthrough = opts.passthrough ?? false;
    this.ricochetAngleDeg = opts.ricochetAngleDeg;
    const flightSec = FLIGHT_DURATION_MS / 1000;
    this.vxPerSec = (this.targetX - this.startX) / flightSec;
    this.vyPerSec = (this.targetY - this.startY) / flightSec;
    this.tickerCb = () => this.update();
  }

  // Starts flight; promise resolves at the moment the ball reaches its target
  // (so the engine's damage event fires at the visual impact point). In
  // passthrough mode the ball keeps flying past the target and self-disposes
  // off-screen; the promise still resolves at the original impact moment.
  flyAndImpact(): Promise<void> {
    return new Promise((resolve) => {
      this.resolveImpact = resolve;
      this.lastTime = performance.now();
      Ticker.shared.add(this.tickerCb);
    });
  }

  private update(): void {
    if (this.destroyed) return;
    const now = performance.now();
    const dt = now - this.lastTime;
    this.lastTime = now;
    this.sprite.rotation += (SPIN_RAD_PER_SEC * dt) / 1000;

    if (!this.impactReached) {
      this.elapsed += dt;
      const t = Math.min(this.elapsed / FLIGHT_DURATION_MS, 1);
      this.x = this.startX + (this.targetX - this.startX) * t;
      this.y = this.startY + (this.targetY - this.startY) * t;
      if (t >= 1) {
        this.impactReached = true;
        // Apply ricochet rotation BEFORE resolving impact promise so the new
        // trajectory is in effect when the next ticker frame runs.
        if (this.passthrough && this.ricochetAngleDeg !== undefined) {
          this.applyRicochet(this.ricochetAngleDeg);
        }
        // Resolve impact promise NOW (engine damage event fires next).
        const resolve = this.resolveImpact;
        this.resolveImpact = null;
        resolve?.();
        if (!this.passthrough) {
          this.dispose();
        }
      }
      return;
    }

    // Passthrough phase: extrapolate trajectory at current velocity (which
    // may be the ricocheted velocity if a bounce was applied at impact).
    this.x += (this.vxPerSec * dt) / 1000;
    this.y += (this.vyPerSec * dt) / 1000;
    if (
      this.x < -OFF_SCREEN_MARGIN ||
      this.x > DESIGN_WIDTH + OFF_SCREEN_MARGIN ||
      this.y < -OFF_SCREEN_MARGIN ||
      this.y > DESIGN_HEIGHT + OFF_SCREEN_MARGIN
    ) {
      this.dispose();
    }
  }

  // Reflects the velocity vector at `angleDeg` between incoming and outgoing.
  // The bounce is biased UPWARD on the screen (negative Y) so it reads as a
  // natural "pinged off armor" deflection. Magnitude is preserved.
  private applyRicochet(angleDeg: number): void {
    const v = Math.hypot(this.vxPerSec, this.vyPerSec);
    if (v === 0) return;
    const shooterFacing = Math.sign(this.targetX - this.startX) || 1;
    // 180° = perfectly antiparallel (straight back). Smaller angle leans
    // outgoing further from "back", more toward perpendicular.
    const deflectionRad = ((180 - angleDeg) * Math.PI) / 180;
    this.vxPerSec = -shooterFacing * v * Math.cos(deflectionRad);
    this.vyPerSec = -v * Math.sin(deflectionRad);
  }

  private dispose(): void {
    // Resolve impact if it hadn't fired yet (defensive - shouldn't happen in
    // normal flow but covers early disposal cases).
    const resolve = this.resolveImpact;
    this.resolveImpact = null;
    this.destroy({ children: true });
    resolve?.();
  }

  // Ticker removal lives in destroy() so scene teardown (a passthrough ball may
  // still be in flight when the battle ends) stops the callback, not just the
  // normal self-dispose path.
  override destroy(options?: Parameters<Container['destroy']>[0]): void {
    Ticker.shared.remove(this.tickerCb);
    super.destroy(options);
  }
}
